import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AdSpace from '@/components/AdSpace';
import HtmlContent from '@/components/HtmlContent';
import SubscribeBox from '@/components/SubscribeBox';
import NativeAd from '@/components/NativeAd';
import HotTopics from '@/components/HotTopics';
import SEO from '@/components/SEO';
import { getArticle, getArticles } from '@/lib/api';
import { Loader2, ArrowLeft, Clock, Eye, Share2, Play } from 'lucide-react';
import { format } from 'date-fns';

const getVideoUrl = (article) => {
  if (article?.video_url) return article.video_url;
  const match = article?.content?.match(/<video[^>]*src="([^"]+)"/);
  if (match) return match[1];
  const sourceMatch = article?.content?.match(/<source[^>]*src="([^"]+)"/);
  return sourceMatch ? sourceMatch[1] : null;
};

const isVideoPost = (article) =>
  article?.post_type === 'video' || article?.category === 'Cincy Worldstar';

const ArticlePage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedVideos, setRelatedVideos] = useState([]);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const response = await getArticle(id);
        setArticle(response.data);

        // If it's a video post, fetch related Worldstar videos
        if (isVideoPost(response.data)) {
          try {
            const related = await getArticles({ category: 'Cincy Worldstar', limit: 20 });
            const filtered = related.data
              .filter((a) => a.id !== response.data.id && a.slug !== id)
              .sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
            setRelatedVideos(filtered);
          } catch (e) {
            console.error('Failed to fetch related videos:', e);
          }
        }
      } catch (error) {
        console.error('Failed to fetch article:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id]);

  const handleShare = async () => {
    const ogUrl = `${window.location.origin}/api/og/article/${article.slug}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: article.title, url: ogUrl });
      } catch (e) {}
    } else {
      navigator.clipboard.writeText(ogUrl);
      alert('Link copied!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
        </div>
      </div>
    );
  }

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const plainContent = article.content?.replace(/<[^>]*>/g, '') || '';
  const description = plainContent.substring(0, 160);
  const formattedDate = article.published_at
    ? format(new Date(article.published_at), 'MMMM d, yyyy')
    : 'Draft';
  const videoUrl = getVideoUrl(article);
  const isVideo = isVideoPost(article);

  return (
    <div className="min-h-screen bg-black page-enter" data-testid="article-page">
      <SEO
        title={article.title}
        description={description}
        image={article.featured_image}
        url={window.location.href}
        type="article"
      />
      <Header />

      {/* ── Back link ── */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        <Link
          to={isVideo ? '/worldstar' : '/'}
          className="inline-flex items-center gap-2 text-gray-400 hover:text-yellow-500 text-sm mb-4 transition-colors"
          data-testid="back-to-feed"
        >
          <ArrowLeft size={16} />
          {isVideo ? 'Back to Worldstar' : 'Back to Feed'}
        </Link>
      </div>

      {/* ── Video Player (video posts only) ── */}
      {isVideo && videoUrl && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 mb-4" data-testid="article-video-player">
          <div className="relative w-full bg-black border border-[#333] overflow-hidden">
            <video
              className="w-full max-h-[70vh] bg-black"
              controls
              poster={article.featured_image || undefined}
              data-testid="video-element"
            >
              <source src={videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}

      {/* ── Ad after nav ── */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
        <AdSpace type="header" />
      </div>

      {/* ── Story Header (title/meta) ── */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <span className="block bg-yellow-600 text-white text-[10px] sm:text-xs font-bold uppercase tracking-widest px-3 py-1 w-fit mb-3">
          {article.category}
        </span>

        <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-4" data-testid="article-title">
          {article.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-sm pb-4 border-b border-[#1a1a1a]">
          <span className="text-white font-medium">{article.author}</span>
          <span className="flex items-center gap-1.5 text-gray-400">
            <Clock size={14} />
            {formattedDate}
          </span>
          <span className="flex items-center gap-1.5 text-green-500 font-medium">
            <Eye size={14} />
            {article.views || 0} views
          </span>
          <button
            onClick={handleShare}
            className="ml-auto flex items-center gap-1.5 px-4 py-1.5 bg-[#1a1a1a] text-white hover:bg-yellow-500 transition-colors rounded-sm text-sm"
            data-testid="share-button"
          >
            <Share2 size={14} />
            Share
          </button>
        </div>
      </div>

      {/* ── Article Body ── */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 pt-6" data-testid="article-content">
        <div className="bg-[#0d0d0d] border border-[#1a1a1a] rounded-sm overflow-hidden">
          <div className="p-5 sm:p-6">
            <HtmlContent
              html={article.content}
              className="text-gray-300 leading-relaxed article-content max-w-none text-base sm:text-lg"
            />

            {!article.content?.includes('<!DOCTYPE') && !article.content?.includes('class="page"') && (
              <div className="mt-4 pt-4 border-t border-[#1a1a1a] flex justify-center" data-testid="mid-article-ad">
                <NativeAd />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* ── Native Ad between content and video list ── */}
      {isVideo && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 mt-4" data-testid="mid-video-ad">
          <NativeAd />
        </div>
      )}

      {/* ── More Videos (video posts only) ── */}
      {isVideo && relatedVideos.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 mt-8" data-testid="related-videos-section">
          <div className="flex items-center gap-2 mb-4 py-2 border-b border-[#333]">
            <Play size={16} className="text-yellow-500" fill="currentColor" />
            <span className="text-white font-bold text-sm">More Videos</span>
            <span className="text-gray-600 text-xs ml-auto">{relatedVideos.length} clips</span>
          </div>

          <div className="flex flex-col gap-3" data-testid="related-videos-list">
            {relatedVideos.map((vid) => (
              <Link
                key={vid.id}
                to={`/article/${vid.slug || vid.id}`}
                className="group block"
                data-testid={`related-video-${vid.id}`}
              >
                <div className="flex bg-[#111] border border-[#222] hover:border-yellow-500/50 transition-all duration-300 overflow-hidden">
                  {/* Thumbnail */}
                  <div className="relative w-[40%] sm:w-[35%] flex-shrink-0 aspect-video overflow-hidden bg-[#0a0a0a]">
                    {vid.featured_image ? (
                      <img
                        src={vid.featured_image}
                        alt={vid.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play size={28} className="text-gray-700" />
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-10 h-10 rounded-full bg-yellow-500/90 flex items-center justify-center">
                        <Play size={18} fill="black" className="text-black ml-0.5" />
                      </div>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 p-2.5 sm:p-3 flex flex-col justify-center min-w-0">
                    <h3 className="text-xs sm:text-sm font-bold text-white group-hover:text-yellow-500 transition-colors leading-tight line-clamp-2 mb-1.5">
                      {vid.title}
                    </h3>
                    <div className="flex items-center gap-3 text-[10px] sm:text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Eye size={10} className="text-yellow-500/70" />
                        {vid.views || 0}
                      </span>
                      <span>
                        {vid.published_at
                          ? new Date(vid.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                          : 'Draft'}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 mt-2">
        <AdSpace type="footer" />
        <HotTopics />
        <div className="mt-4">
          <SubscribeBox />
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ArticlePage;
